/** Preserve the full same-origin path, including its query, across /login. */

const SESSION_KEY = 'ui.auth.next';
const SAFE_PATH_BASE = 'https://auth-redirect.invalid';

export function pathWithSearch(pathname: string, search: string): string {
  const path = String(pathname || '/');
  const query = String(search || '');
  if (!query || query === '?') return path;
  return `${path}${query.startsWith('?') ? query : `?${query}`}`;
}

function pagePath(value: string): string {
  const path = String(value || '').split('?')[0].split('#')[0];
  if (path.length > 1 && path.endsWith('/')) return path.replace(/\/+$/, '') || '/';
  return path || '/';
}

export function isSafeInternalPath(value: string | null | undefined): boolean {
  const path = String(value || '').trim();
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//')) return false;
  if (path.includes('\\') || path.includes('\n') || path.includes('\r') || path.includes('\t')) return false;
  if (path.toLowerCase().startsWith('/login')) return false;
  try {
    const resolved = new URL(path, SAFE_PATH_BASE);
    if (resolved.origin !== SAFE_PATH_BASE) return false;
  } catch {
    return false;
  }
  return true;
}

/**
 * Keep the complete relative destination. If the router location dropped the
 * query while the browser URL still has it for the same page, use the browser URL.
 */
export function destinationForAuthRedirect(pathname: string, search: string): string {
  const routerPath = pathWithSearch(pathname, search);
  if (typeof window === 'undefined') return routerPath;
  let browserPath = routerPath;
  try {
    const url = new URL(window.location.href);
    browserPath = pathWithSearch(url.pathname, url.search);
  } catch {
    browserPath = pathWithSearch(
      String(window.location.pathname || '/'),
      String(window.location.search || ''),
    );
  }
  const routerSafe = isSafeInternalPath(routerPath) ? routerPath : '';
  const browserSafe = isSafeInternalPath(browserPath) ? browserPath : '';
  if (routerSafe.includes('?')) return routerSafe;
  if (browserSafe.includes('?') && pagePath(browserSafe) === pagePath(pathname || '/')) return browserSafe;
  return routerSafe || browserSafe || '/';
}

export function buildLoginRedirectPath(currentPathWithSearch: string): string {
  const next = String(currentPathWithSearch || '').trim() || '/';
  if (!isSafeInternalPath(next) || next === '/') return '/login';
  return `/login?next=${encodeURIComponent(next)}`;
}

export function buildLoginRedirectTo(currentPathWithSearch: string): { pathname: '/login'; search: string } {
  const built = buildLoginRedirectPath(currentPathWithSearch);
  const queryIndex = built.indexOf('?');
  if (queryIndex < 0) return { pathname: '/login', search: '' };
  return { pathname: '/login', search: built.slice(queryIndex) };
}

export function routerTargetFromInternalPath(pathWithSearchValue: string): { pathname: string; search: string } {
  const safe = isSafeInternalPath(pathWithSearchValue) ? pathWithSearchValue.trim() : '/';
  const queryIndex = safe.indexOf('?');
  if (queryIndex < 0) return { pathname: safe || '/', search: '' };
  return { pathname: safe.slice(0, queryIndex) || '/', search: safe.slice(queryIndex) };
}

export function rememberPostLoginPath(pathWithSearchValue: string): void {
  if (typeof window === 'undefined') return;
  if (!isSafeInternalPath(pathWithSearchValue)) return;
  try {
    sessionStorage.setItem(SESSION_KEY, pathWithSearchValue);
  } catch {
    // ignore quota / private mode
  }
}

export function consumePostLoginPath(search?: string): string {
  const params = new URLSearchParams(String(search || (typeof window !== 'undefined' ? window.location.search : '') || ''));
  const fromQuery = String(params.get('next') || '').trim();
  if (isSafeInternalPath(fromQuery)) {
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      // ignore
    }
    return fromQuery;
  }
  try {
    const stored = String(sessionStorage.getItem(SESSION_KEY) || '').trim();
    sessionStorage.removeItem(SESSION_KEY);
    if (isSafeInternalPath(stored)) return stored;
  } catch {
    // ignore
  }
  return '/';
}
